import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useState, useEffect } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { InputText } from 'primereact/inputtext';
import { handlePostRequest } from '../../services/PostTemplate';
import classNames from 'classnames';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Dropdown } from 'primereact/dropdown';

function TransactionLedger() {
    const [reportsData, setReportData] = useState([]);
    const [rawReportsData, setRawReportsData] = useState([]);
    const [transactionOptions] = useState([
        { label: 'All', value: null },
        { label: 'Credit', value: 'Credit' },
        { label: 'Debit', value: 'Debit' }
    ]);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    const dispatch = useDispatch();

    const formatDate = (d) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const formatDateTime = (iso) => {
        if (!iso) return '';
        try {
            return new Date(iso).toLocaleString();
        } catch (e) {
            return iso;
        }
    };

    // compute current week's Monday (start) and Sunday (end)
    const getCurrentWeekRange = () => {
        const now = new Date();
        // getDay: 0 (Sun) - 6 (Sat). We want Monday as start.
        const day = now.getDay();
        // distance to Monday
        const diffToMonday = (day + 6) % 7; // if day=1(Mon)->0, day=0(Sun)->6
        const monday = new Date(now);
        monday.setDate(now.getDate() - diffToMonday);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        return { weekStart: formatDate(monday), weekEnd: formatDate(sunday) };
    };

    // no zones fetch: using static transaction direction options

    const { weekStart, weekEnd } = getCurrentWeekRange();

    const formik = useFormik({
        initialValues: {
            fromDate: weekStart || null,
            toDate: weekEnd || null,
            transactionDirection: null,
            userName: '',
            consumerId: '',
            transactionId: ''
        },
        onSubmit: async (data) => {
            // handled by explicit Search button
        }
    });

    const [loadingReports, setLoadingReports] = useState(false);

    const fetchReports = async (values = formik.values) => {
        setLoadingReports(true);
        try {
            // Backend expects query params (FromQuery): fromDate, toDate, Username, TransactionId, ConsumerId, TransactionDirection, pageNumber, pageSize
            const qs = [];
            const from = values?.fromDate || '';
            const to = values?.toDate || '';
            const Username = values?.userName || '';
            const TransactionId = values?.transactionId || '';
            const ConsumerId = values?.consumerId || '';
            const TransactionDirection = values?.transactionDirection || '';
            qs.push(`fromDate=${encodeURIComponent(from)}`);
            qs.push(`toDate=${encodeURIComponent(to)}`);
            qs.push(`Username=${encodeURIComponent(Username)}`);
            qs.push(`TransactionId=${encodeURIComponent(TransactionId)}`);
            qs.push(`ConsumerId=${encodeURIComponent(ConsumerId)}`);
            qs.push(`TransactionDirection=${encodeURIComponent(TransactionDirection)}`);
            qs.push(`pageNumber=${encodeURIComponent(pageNumber)}`);
            qs.push(`pageSize=${encodeURIComponent(pageSize)}`);

            const payload = {
                userName: values?.userName ? values.userName : null,
                consumerId: values?.consumerId ? values.consumerId : null,
                transactionId: values?.transactionId ? values.transactionId : null,
                fromDate: values?.fromDate ? values.fromDate : null,
                toDate: values?.toDate ? values.toDate : null,
                transactionDirection: values?.transactionDirection ?? null,
                pageNumber: Number(pageNumber) || 1,
                pageSize: Number(pageSize) || 10
            };

            const response = await dispatch(handlePostRequest(payload, 'revenueReport/TransactionLedger', false, false, '', '', false));

            // Normalize different possible response shapes and ensure array for DataTable
            let data = [];
            if (response == null) {
                data = [];
            } else if (response?.success === true) {
                // API convention: { success:true, data:[...], totalRecords: n }
                data = Array.isArray(response.data) ? response.data : response.data?.items ?? response.data ?? [];
            } else if (Array.isArray(response)) {
                data = response;
            } else {
                data = Array.isArray(response?.data) ? response.data : response?.data ?? [];
            }

            setRawReportsData(data || []);
            setReportData(data || []);
            setTotalRecords(response?.data?.totalCount ?? response?.totalRecords ?? response?.data?.totalRecords ?? (Array.isArray(data) ? data.length : data?.length ?? 0));
        } catch (err) {
            setReportData([]);
            setRawReportsData([]);
            setTotalRecords(0);
        } finally {
            setLoadingReports(false);
        }
    };

    // Fetch when pagination changes
    useEffect(() => {
        // only fetch when page changes (user initiated search should set pageNumber=1)
        if (formik.values.fromDate && formik.values.toDate) {
            fetchReports();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, pageSize]);

    // TransactionDirection filtering is handled by backend; no client-side filtering here.
    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    const renderField = (field) => {
        return (row) => {
            const val = row?.[field];
            return val === null || val === undefined || val === '' ? '-' : val;
        };
    };

    const handlePrint = () => {
        const doc = new jsPDF('p', 'pt', 'a4');

        doc.text('Transaction Ledger', 40, 40);

        const tableColumn = ['User', 'Payment Source', 'Transaction ID', 'Consumer ID', 'Amount', 'Direction', 'Opening Balance', 'Closing Balance', 'Date'];
        const tableRows = [];

        (reportsData || []).forEach((item) => {
            tableRows.push([
                // item.ledgerId,
                // item.walletID,
                // item.topupId,
                item.userName || '-',
                item.paymentSourceName || '-',
                item.transactionId || '-',
                item.consumerId || '-',
                item.amount !== null && item.amount !== undefined ? item.amount : '-',
                item.transactionDirection || '-',
                item.openingBalance !== null && item.openingBalance !== undefined ? item.openingBalance : '-',
                item.closingBalance !== null && item.closingBalance !== undefined ? item.closingBalance : '-',
                formatDateTime(item.createdOn)
            ]);
        });

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 60
        });

        doc.save('transaction-ledger.pdf');
    };

    const date = new Date().toISOString().split('T')[0];

    return (
        <>
            <div className="grid">
                <div className="col-12">
                    <div className="Card__Round pt-5" style={{ border: 'none' }}>
                        <div className="flex justify-between items-center w-full">
                            <div>
                                <h3 className="mb-0">Transaction Ledger</h3>
                                <p className="mt-0" style={{ fontSize: '20px' }}>
                                    View detailed transaction and revenue metrics for each zone
                                </p>
                            </div>

                            <div className="flex gap-2 justify-end ml-auto" style={{ width: '30%' }}>
                                <Button label="Export" icon="pi pi-upload" className="p-button-secondary Black__Btn ml-auto" onClick={handlePrint} />
                            </div>
                        </div>
                        <form onSubmit={formik.handleSubmit} className="p-fluid p-mt-2">
                            <div className="p-fluid p-formgrid grid mb-5 col-12 md:col-12">
                                <div className="p-fluid p-formgrid grid col-12 md:col-4">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="fromDate" className={classNames({ 'p-erro': isFormFieldValid('fromDate') }, 'Label__Text')}>
                                                Start Date <span className="Label__Required">*</span>
                                            </label>
                                            <InputText
                                                max={date}
                                                type="date"
                                                id="fromDate"
                                                name="fromDate"
                                                value={formik?.values?.fromDate || ''}
                                                onChange={(e) => formik.setFieldValue('fromDate', e.target.value === '' ? null : e.target.value)}
                                                className={classNames({ 'p-invalid': isFormFieldValid('fromDate') }, 'Input__Round mt-1')}
                                            />
                                            {getFormErrorMessage('fromDate')}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-fluid p-formgrid grid col-12 md:col-4">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="toDate" className={classNames({ 'p-erro': isFormFieldValid('toDate') }, 'Label__Text')}>
                                                End Date <span className="Label__Required">*</span>
                                            </label>
                                            <InputText
                                                min={formik.values.fromDate}
                                                max={date}
                                                type="date"
                                                id="toDate"
                                                name="toDate"
                                                value={formik?.values?.toDate || ''}
                                                onChange={(e) => formik.setFieldValue('toDate', e.target.value === '' ? null : e.target.value)}
                                                className={classNames({ 'p-invalid': isFormFieldValid('toDate') }, 'Input__Round mt-1')}
                                            />
                                            {getFormErrorMessage('toDate')}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-fluid p-formgrid grid col-12 md:col-4">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="transactionDirection" className={classNames({ 'p-erro': isFormFieldValid('transactionDirection') }, 'Label__Text')}>
                                                Transaction Direction <span className="Label__Required">*</span>
                                            </label>
                                            <Dropdown
                                                placeholder="All"
                                                options={transactionOptions}
                                                id="transactionDirection"
                                                name="transactionDirection"
                                                value={formik?.values?.transactionDirection ?? ''}
                                                onChange={(e) => formik.setFieldValue('transactionDirection', e.value === '' ? null : e.value)}
                                                optionLabel="label"
                                                optionValue="value"
                                                className={classNames({ 'p-invalid': isFormFieldValid('transactionDirection') }, 'Dropdown__Round mt-1')}
                                            />
                                            {getFormErrorMessage('transactionDirection')}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-fluid p-formgrid grid col-12 md:col-4">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="userName" className="Label__Text">
                                                Username
                                            </label>
                                            <InputText
                                                id="userName"
                                                name="userName"
                                                value={formik.values.userName || ''}
                                                onChange={(e) => formik.setFieldValue('userName', e.target.value)}
                                                placeholder="Username"
                                                className={classNames('Input__Round mt-1')}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="p-fluid p-formgrid grid col-12 md:col-4">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="consumerId" className="Label__Text">
                                                Consumer ID
                                            </label>
                                            <InputText
                                                id="consumerId"
                                                name="consumerId"
                                                value={formik.values.consumerId || ''}
                                                onChange={(e) => formik.setFieldValue('consumerId', e.target.value)}
                                                placeholder="Consumer ID"
                                                className={classNames('Input__Round mt-1')}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="p-fluid p-formgrid grid col-12 md:col-4">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <label htmlFor="transactionId" className="Label__Text">
                                                Transaction ID
                                            </label>
                                            <InputText
                                                id="transactionId"
                                                name="transactionId"
                                                value={formik.values.transactionId || ''}
                                                onChange={(e) => formik.setFieldValue('transactionId', e.target.value)}
                                                placeholder="Transaction ID"
                                                className={classNames('Input__Round mt-1')}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="p-fluid p-formgrid grid col-12 md:col-4">
                                    <div className="p-field col-12 md:col-12 pt-3">
                                        <div className="p-field">
                                            <Button
                                                label="Search"
                                                className="Btn__Dark mt-4"
                                                style={{ height: '40px', width: '180px' }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setPageNumber(1);
                                                    fetchReports(formik.values);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className="Table__Section mt-6" style={{ position: 'relative' }}>
                            {loadingReports && (
                                <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                                </div>
                            )}
                            <DataTable
                                paginator
                                lazy
                                loading={loadingReports}
                                first={(pageNumber - 1) * pageSize}
                                rows={pageSize}
                                rowsPerPageOptions={[10, 25, 50]}
                                totalRecords={totalRecords}
                                onPage={(e) => {
                                    const newPage = Math.floor(e.first / e.rows) + 1;
                                    setPageNumber(newPage);
                                    setPageSize(e.rows);
                                }}
                                className="datatable-responsive"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Records"
                                emptyMessage="No List found."
                                responsiveLayout="scroll"
                                value={Array.isArray(reportsData) ? reportsData : []}
                            >
                                {/* <Column header="Ledger ID" body={renderField('ledgerId')} />
                                <Column header="Wallet ID" body={renderField('walletID')} />
                                <Column header="Topup ID" body={renderField('topupId')} /> */}
                                <Column header="User" body={renderField('userName')} />
                                <Column header="Payment Source" body={renderField('paymentSourceName')} />
                                <Column header="Transaction ID" body={renderField('transactionId')} />
                                <Column header="Consumer ID" body={renderField('consumerId')} />
                                <Column
                                    header="Amount"
                                    body={(row) => {
                                        const a = row?.amount;
                                        return a === null || a === undefined || a === '' ? '-' : `PKR ${a}`;
                                    }}
                                />
                                <Column
                                    header="Direction"
                                    body={(row) => {
                                        const dir = row?.transactionDirection;
                                        const bg = dir === 'Debit' ? '#fff59d' : dir === 'Credit' ? '#b9f6ca' : 'transparent';
                                        const style = { background: bg, padding: '4px 8px', borderRadius: '4px', display: 'inline-block' };
                                        return <span style={style}>{dir || '-'}</span>;
                                    }}
                                />
                                <Column header="Opening Balance" body={renderField('openingBalance')} />
                                <Column header="Closing Balance" body={renderField('closingBalance')} />
                                <Column header="Date" body={(row) => (row?.createdOn ? formatDateTime(row.createdOn) : '-')} />
                            </DataTable>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default TransactionLedger;
